import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { Search, Mail, Phone, MessageCircle, Upload, Send, X, ChevronDown, Check, AlertCircle, Users } from 'lucide-react';
import { formatPlaceName } from '../utils/placeName';

const STATUS_COLORS = {
  not_contacted: 'bg-gray-100 text-gray-700',
  contacted: 'bg-blue-100 text-blue-700',
  interested: 'bg-amber-100 text-amber-700',
  claimed: 'bg-green-100 text-green-700',
  not_interested: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  not_contacted: 'Not Contacted',
  contacted: 'Contacted',
  interested: 'Interested',
  claimed: 'Claimed',
  not_interested: 'Not Interested',
};

const DEFAULT_WA_TEMPLATE_EN = `Hi! Your business *{{businessName}}* is already listed on Find-2-Day, Almería's local business directory. Claim it in 60 seconds and start connecting with new customers: https://www.find-2-day.com/claim-your-business`;

const DEFAULT_WA_TEMPLATE_ES = `Hola! Tu negocio *{{businessName}}* ya aparece en Find-2-Day, el directorio de negocios locales de Almería. Reclámalo en 60 segundos y empieza a conectar con nuevos clientes: https://www.find-2-day.com/claim-your-business`;

const AdminOutreach = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [csvModal, setCsvModal] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [batchSending, setBatchSending] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [sendingId, setSendingId] = useState(null);
  const [waTemplateModal, setWaTemplateModal] = useState(false);
  const [waTemplateEN, setWaTemplateEN] = useState('');
  const [waTemplateES, setWaTemplateES] = useState('');
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlinePhone, setInlinePhone] = useState('');
  const [inlinePhoneType, setInlinePhoneType] = useState('mobile');
  const [inlineSaving, setInlineSaving] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const fileInputRef = useRef(null);
  const inlineInputRef = useRef(null);

  useEffect(() => {
    fetchStats();
    fetchBusinesses();
    fetchWaTemplates();
  }, [statusFilter]);

  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get('/admin/outreach/stats');
      setStats(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axiosInstance.get('/admin/outreach/businesses', { params });
      setBusinesses(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const filteredBusinesses = businesses.filter(b =>
    !searchTerm || b.businessName.toLowerCase().includes(searchTerm.toLowerCase()) || (b.city && b.city.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const fetchWaTemplates = async () => {
    try {
      const res = await axiosInstance.get('/admin/outreach/wa-template');
      setWaTemplateEN(res.data.templateEN || DEFAULT_WA_TEMPLATE_EN);
      setWaTemplateES(res.data.templateES || DEFAULT_WA_TEMPLATE_ES);
    } catch {
      setWaTemplateEN(DEFAULT_WA_TEMPLATE_EN);
      setWaTemplateES(DEFAULT_WA_TEMPLATE_ES);
    }
  };

  const handleSaveWaTemplate = async () => {
    try {
      await axiosInstance.put('/admin/outreach/wa-template', {
        templateEN: waTemplateEN,
        templateES: waTemplateES,
      });
      showMsg('success', 'WhatsApp templates saved');
      setWaTemplateModal(false);
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Failed to save templates');
    }
  };

  const openWhatsApp = async (business) => {
    const phone = (business.contactWhatsApp || business.contactPhone || '').replace(/[^0-9+]/g, '');
    if (!phone) {
      showMsg('error', `No WhatsApp/phone number for ${business.businessName}. Add one first.`);
      return;
    }
    const cleanPhone = phone.startsWith('+') ? phone.slice(1) : phone;
    const msg = (waTemplateEN || DEFAULT_WA_TEMPLATE_EN).replace(/\{\{businessName\}\}/g, business.businessName);
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');

    // Track the WhatsApp outreach - update local state optimistically
    const updatedBusinesses = businesses.map(b =>
      b.id === business.id
        ? { ...b, outreachStatus: b.outreachStatus === 'not_contacted' ? 'contacted' : b.outreachStatus, outreachMethod: 'whatsapp' }
        : b
    );
    setBusinesses(updatedBusinesses);

    // Auto-advance to next business without a phone
    const currentFiltered = updatedBusinesses.filter(b =>
      !searchTerm || b.businessName.toLowerCase().includes(searchTerm.toLowerCase()) || (b.city && b.city.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const currentIdx = currentFiltered.findIndex(b => b.id === business.id);
    const nextWithoutPhone = currentFiltered.find((b, i) => i > currentIdx && (!b.contactWhatsApp && !b.contactPhone) || (b.phoneType === 'landline' && !b.contactWhatsApp));
    if (nextWithoutPhone) {
      setHighlightId(nextWithoutPhone.id);
      setTimeout(() => {
        const row = document.querySelector(`[data-testid="outreach-row-${nextWithoutPhone.id}"]`);
        row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Auto-open inline edit on the next row
        setInlineEditId(nextWithoutPhone.id);
        setInlinePhone('');
        setTimeout(() => inlineInputRef.current?.focus(), 100);
      }, 200);
      showMsg('success', `WhatsApp sent. Next: ${nextWithoutPhone.businessName}`);
    } else {
      setHighlightId(null);
      showMsg('success', 'WhatsApp sent. No more businesses without a phone number!');
    }

    // Track in backend (non-blocking)
    axiosInstance.put(`/admin/outreach/business/${business.id}`, {
      outreachStatus: business.outreachStatus === 'not_contacted' ? 'contacted' : business.outreachStatus,
      outreachMethod: 'whatsapp',
    }).then(() => fetchStats()).catch(() => {});
  };

  const startInlineEdit = (business) => {
    setInlineEditId(business.id);
    setInlinePhone(business.contactWhatsApp || business.contactPhone || '');
    setInlinePhoneType(business.phoneType || 'mobile');
    setTimeout(() => inlineInputRef.current?.focus(), 50);
  };

  const saveInlinePhone = async (businessId) => {
    const phone = inlinePhone.trim();
    if (!phone) { setInlineEditId(null); return; }
    const formatted = phone.startsWith('+') ? phone : phone.startsWith('34') ? `+${phone}` : `+34${phone.replace(/^0+/, '')}`;
    setInlineSaving(true);
    try {
      const updateData = { contactPhone: formatted, phoneType: inlinePhoneType };
      if (inlinePhoneType === 'mobile') updateData.contactWhatsApp = formatted;
      else updateData.contactWhatsApp = '';
      await axiosInstance.put(`/admin/outreach/business/${businessId}`, updateData);
      setBusinesses(prev => prev.map(b =>
        b.id === businessId ? {
          ...b,
          contactPhone: formatted,
          contactWhatsApp: inlinePhoneType === 'mobile' ? formatted : '',
          phoneType: inlinePhoneType,
        } : b
      ));
      setHighlightId(businessId);
      showMsg('success', `Saved ${formatted} (${inlinePhoneType})${inlinePhoneType === 'mobile' ? ' — click WhatsApp to send' : ' — use email for this one'}`);
    } catch (err) {
      showMsg('error', 'Failed to save number');
    }
    setInlineSaving(false);
    setInlineEditId(null);
  };

  const handleInlineKeyDown = (e, businessId) => {
    if (e.key === 'Enter') { e.preventDefault(); saveInlinePhone(businessId); }
    if (e.key === 'Escape') { setInlineEditId(null); }
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleUpdateOutreach = async (formData) => {
    try {
      await axiosInstance.put(`/admin/outreach/business/${editModal.id}`, formData);
      showMsg('success', `Updated ${editModal.businessName}`);
      setEditModal(null);
      fetchBusinesses();
      fetchStats();
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Error updating');
    }
  };

  const handleSendEmail = async (business) => {
    const email = business.contactEmail || business.email;
    if (!email || email === 'find-2-day@outlook.com') {
      showMsg('error', `No valid email for ${business.businessName}. Add a contact email first.`);
      return;
    }
    if (email.endsWith('@listing.find-2-day.com')) {
      showMsg('error', `${business.businessName} has a placeholder email. Add a real contact email in the Edit panel first.`);
      return;
    }
    setSendingId(business.id);
    try {
      const res = await axiosInstance.post(`/admin/outreach/send-email/${business.id}`, { email });
      showMsg('success', `Email sent to ${res.data.sentTo}`);
      fetchBusinesses();
      fetchStats();
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Failed to send email');
    }
    setSendingId(null);
  };

  const handleBatchSend = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBatchSending(true);
    try {
      const res = await axiosInstance.post('/admin/outreach/send-batch', { businessIds: ids });
      setBatchResult(res.data);
      showMsg('success', `Batch: ${res.data.sent} sent, ${res.data.skipped} skipped, ${res.data.failed} failed`);
      setSelected(new Set());
      fetchBusinesses();
      fetchStats();
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Batch send failed');
    }
    setBatchSending(false);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axiosInstance.post('/admin/outreach/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCsvResult(res.data);
      fetchBusinesses();
      showMsg('success', `CSV imported: ${res.data.updated} updated, ${res.data.skipped} skipped`);
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'CSV import failed');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredBusinesses.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredBusinesses.map(b => b.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  return (
    <div>
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Total', count: stats.total, color: 'bg-purple-50 text-purple-700 border-purple-200', filter: 'all' },
            { label: STATUS_LABELS.not_contacted, count: stats.not_contacted, color: 'bg-gray-50 text-gray-700 border-gray-200', filter: 'not_contacted' },
            { label: STATUS_LABELS.contacted, count: stats.contacted, color: 'bg-blue-50 text-blue-700 border-blue-200', filter: 'contacted' },
            { label: STATUS_LABELS.interested, count: stats.interested, color: 'bg-amber-50 text-amber-700 border-amber-200', filter: 'interested' },
            { label: STATUS_LABELS.claimed, count: stats.claimed, color: 'bg-green-50 text-green-700 border-green-200', filter: 'claimed' },
            { label: STATUS_LABELS.not_interested, count: stats.not_interested, color: 'bg-red-50 text-red-700 border-red-200', filter: 'not_interested' },
          ].map(s => (
            <button
              key={s.filter}
              onClick={() => setStatusFilter(s.filter)}
              className={`p-3 rounded-lg border text-center transition-all ${s.color} ${statusFilter === s.filter ? 'ring-2 ring-purple-400 shadow' : 'hover:shadow'}`}
              data-testid={`outreach-stat-${s.filter}`}
            >
              <div className="text-2xl font-bold">{s.count}</div>
              <div className="text-xs font-medium">{s.label}</div>
            </button>
          ))}
        </div>
      )}

      {/* Message */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${message.type === 'error' ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-green-100 text-green-800 border border-green-300'}`} data-testid="outreach-message">
          {message.text}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
            data-testid="outreach-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setCsvModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            data-testid="csv-import-btn"
          >
            <Upload className="h-4 w-4" />
            Import CSV
          </button>
          <button
            onClick={() => setWaTemplateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-300 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
            data-testid="wa-template-btn"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp Template
          </button>
          {selected.size > 0 && (
            <button
              onClick={handleBatchSend}
              disabled={batchSending}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
              data-testid="batch-send-btn"
            >
              <Send className="h-4 w-4" />
              {batchSending ? 'Sending...' : `Send to ${selected.size} selected`}
            </button>
          )}
        </div>
      </div>

      {/* Business Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left">
                  <input
                    type="checkbox"
                    checked={selected.size === filteredBusinesses.length && filteredBusinesses.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="p-3 text-left font-medium text-gray-700">Business</th>
                <th className="p-3 text-left font-medium text-gray-700 hidden md:table-cell">Category</th>
                <th className="p-3 text-left font-medium text-gray-700 hidden sm:table-cell">City</th>
                <th className="p-3 text-left font-medium text-gray-700">Contact</th>
                <th className="p-3 text-left font-medium text-gray-700">Status</th>
                <th className="p-3 text-left font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-gray-500">Loading...</td></tr>
              ) : filteredBusinesses.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-gray-500">No businesses found</td></tr>
              ) : filteredBusinesses.map(b => (
                <tr
                  key={b.id}
                  className={`transition-colors ${highlightId === b.id ? 'bg-green-50 ring-2 ring-green-400 ring-inset' : 'hover:bg-gray-50'}`}
                  data-testid={`outreach-row-${b.id}`}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selected.has(b.id)}
                      onChange={() => toggleSelect(b.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-gray-900">{b.businessName}</div>
                    {b.outreachNotes && <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{b.outreachNotes}</div>}
                  </td>
                  <td className="p-3 hidden md:table-cell text-gray-600">{b.category}</td>
                  <td className="p-3 hidden sm:table-cell text-gray-600">{formatPlaceName(b.city)}</td>
                  <td className="p-3">
                    {inlineEditId === b.id ? (
                      /* Inline edit mode */
                      <div className="flex items-center gap-1 flex-wrap" data-testid={`inline-edit-${b.id}`}>
                        <input
                          ref={inlineInputRef}
                          type="tel"
                          value={inlinePhone}
                          onChange={(e) => setInlinePhone(e.target.value)}
                          onKeyDown={(e) => handleInlineKeyDown(e, b.id)}
                          onBlur={() => { if (!inlineSaving && !document.activeElement?.dataset?.phonetype) saveInlinePhone(b.id); }}
                          placeholder="+34 600 000 000"
                          className="w-32 px-2 py-1 border border-green-400 rounded text-xs focus:ring-2 focus:ring-green-400 focus:outline-none"
                          data-testid={`inline-phone-input-${b.id}`}
                          disabled={inlineSaving}
                        />
                        <button
                          data-phonetype="true"
                          onClick={() => setInlinePhoneType(inlinePhoneType === 'mobile' ? 'landline' : 'mobile')}
                          className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                            inlinePhoneType === 'mobile'
                              ? 'bg-green-100 text-green-700 border-green-300'
                              : 'bg-gray-100 text-gray-600 border-gray-300'
                          }`}
                          title={`Type: ${inlinePhoneType}. Click to toggle.`}
                          data-testid={`phone-type-toggle-${b.id}`}
                        >
                          {inlinePhoneType === 'mobile' ? <><MessageCircle className="h-3 w-3 inline mr-1" />Mobile</> : <><Phone className="h-3 w-3 inline mr-1" />Landline</>}
                        </button>
                        {inlineSaving && <span className="text-xs text-gray-400">...</span>}
                      </div>
                    ) : (
                      /* Display mode */
                      <div className="flex flex-col gap-0.5">
                        {(b.contactEmail && b.contactEmail !== 'find-2-day@outlook.com') && (
                          <span className="flex items-center gap-1 text-xs text-gray-600">
                            <Mail className="h-3 w-3" />{b.contactEmail}
                          </span>
                        )}
                        {(b.contactWhatsApp || b.contactPhone) ? (
                          <button
                            onClick={() => startInlineEdit(b)}
                            className={`flex items-center gap-1 text-xs text-left cursor-pointer hover:underline ${
                              b.phoneType === 'landline' ? 'text-gray-500' : 'text-green-600 hover:text-green-800'
                            }`}
                            title={`${b.phoneType === 'landline' ? 'Landline' : 'Mobile'} — click to edit`}
                            data-testid={`phone-display-${b.id}`}
                          >
                            {b.phoneType === 'landline'
                              ? <><Phone className="h-3 w-3" />{b.contactPhone}<span className="text-gray-400 ml-1">(landline)</span></>
                              : <><MessageCircle className="h-3 w-3" />{b.contactWhatsApp || b.contactPhone}</>
                            }
                          </button>
                        ) : (
                          <button
                            onClick={() => startInlineEdit(b)}
                            className="flex items-center gap-1 text-xs text-amber-500 hover:text-amber-700 font-medium cursor-pointer"
                            data-testid={`add-phone-btn-${b.id}`}
                          >
                            <Phone className="h-3 w-3" />+ Add phone
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[b.outreachStatus]}`} data-testid={`status-badge-${b.id}`}>
                      {STATUS_LABELS[b.outreachStatus]}
                    </span>
                    {b.outreachMethod && (
                      <div className="text-xs text-gray-400 mt-0.5 capitalize">{b.outreachMethod.replace('_', ' ')}</div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditModal(b)}
                        className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100 transition-colors"
                        data-testid={`edit-btn-${b.id}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleSendEmail(b)}
                        disabled={sendingId === b.id || (!b.contactEmail && !b.email) || (b.contactEmail === 'find-2-day@outlook.com' && (!b.email || b.email === 'find-2-day@outlook.com')) || ((b.contactEmail || b.email || '').endsWith('@listing.find-2-day.com'))}
                        className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={((b.contactEmail || b.email || '').endsWith('@listing.find-2-day.com')) ? 'Placeholder email — add a real contact email first' : 'Send Email'}
                        data-testid={`send-email-btn-${b.id}`}
                      >
                        {sendingId === b.id ? '...' : <Mail className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => openWhatsApp(b)}
                        disabled={b.phoneType === 'landline' || (!b.contactWhatsApp && !b.contactPhone)}
                        className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={b.phoneType === 'landline' ? 'Landline — use email instead' : 'Open WhatsApp'}
                        data-testid={`wa-btn-${b.id}`}
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && <EditOutreachModal business={editModal} onClose={() => setEditModal(null)} onSave={handleUpdateOutreach} />}

      {/* CSV Import Modal */}
      {csvModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6" data-testid="csv-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Import Contact Details (CSV)</h3>
              <button onClick={() => { setCsvModal(false); setCsvResult(null); }} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Upload a CSV with columns: <code className="bg-gray-100 px-1 rounded">businessName</code>, <code className="bg-gray-100 px-1 rounded">contactEmail</code>, <code className="bg-gray-100 px-1 rounded">contactPhone</code>, <code className="bg-gray-100 px-1 rounded">contactWhatsApp</code>
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Business names must match exactly (case-insensitive). Only seed businesses will be updated.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleCsvUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 mb-4"
              data-testid="csv-file-input"
            />
            {csvResult && (
              <div className="bg-gray-50 rounded-lg p-4 text-sm" data-testid="csv-result">
                <p className="font-medium text-green-700">Updated: {csvResult.updated}</p>
                <p className="text-gray-600">Skipped: {csvResult.skipped}</p>
                {csvResult.errors.length > 0 && (
                  <div className="mt-2 text-red-600">
                    {csvResult.errors.map((e, i) => <p key={i} className="text-xs">{e}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Batch Result Modal */}
      {batchResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6" data-testid="batch-result-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Batch Send Results</h3>
              <button onClick={() => setBatchResult(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-700">{batchResult.sent}</div>
                <div className="text-xs text-green-600">Sent</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-700">{batchResult.skipped}</div>
                <div className="text-xs text-gray-600">Skipped</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-700">{batchResult.failed}</div>
                <div className="text-xs text-red-600">Failed</div>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto text-xs space-y-1">
              {batchResult.details.map((d, i) => (
                <div key={i} className={`flex justify-between p-2 rounded ${d.status === 'sent' || d.status === 'logged' ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <span>{d.name || d.id}</span>
                  <span className={`font-medium ${d.status === 'no_email' ? 'text-amber-600' : d.status === 'sent' || d.status === 'logged' ? 'text-green-600' : 'text-red-600'}`}>
                    {d.status === 'no_email' ? 'No email' : d.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Template Modal */}
      {waTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full p-6" data-testid="wa-template-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-green-600" />
                WhatsApp Message Template
              </h3>
              <button onClick={() => setWaTemplateModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Use <code className="bg-gray-100 px-1 rounded text-xs">{'{{businessName}}'}</code> as a placeholder — it will be replaced with the actual business name.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">English Template</label>
                <textarea
                  value={waTemplateEN}
                  onChange={(e) => setWaTemplateEN(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                  rows={4}
                  data-testid="wa-template-en"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spanish Template</label>
                <textarea
                  value={waTemplateES}
                  onChange={(e) => setWaTemplateES(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                  rows={4}
                  data-testid="wa-template-es"
                />
              </div>

              {/* Preview */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs font-medium text-green-700 mb-2">Preview (English):</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {waTemplateEN.replace(/\{\{businessName\}\}/g, 'Casa Puga')}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => { setWaTemplateEN(DEFAULT_WA_TEMPLATE_EN); setWaTemplateES(DEFAULT_WA_TEMPLATE_ES); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                data-testid="wa-template-reset"
              >
                Reset to Default
              </button>
              <button
                onClick={handleSaveWaTemplate}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                data-testid="wa-template-save"
              >
                Save Templates
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const EditOutreachModal = ({ business, onClose, onSave }) => {
  const [form, setForm] = useState({
    outreachStatus: business.outreachStatus || 'not_contacted',
    outreachMethod: business.outreachMethod || '',
    contactEmail: business.contactEmail || '',
    contactPhone: business.contactPhone || '',
    contactWhatsApp: business.contactWhatsApp || '',
    outreachNotes: business.outreachNotes || '',
  });

  const handleSave = () => {
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6" data-testid="edit-outreach-modal">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{business.businessName}</h3>
            <p className="text-sm text-gray-500">{business.category} — {formatPlaceName(business.city)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Outreach Status</label>
            <select
              value={form.outreachStatus}
              onChange={e => setForm({ ...form, outreachStatus: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
              data-testid="edit-status-select"
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {/* Contact Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Method</label>
            <select
              value={form.outreachMethod}
              onChange={e => setForm({ ...form, outreachMethod: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
              data-testid="edit-method-select"
            >
              <option value="">None</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="in_person">In Person</option>
            </select>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <Mail className="inline h-3 w-3 mr-1" />Email
              </label>
              <input
                type="email"
                value={form.contactEmail}
                onChange={e => setForm({ ...form, contactEmail: e.target.value })}
                placeholder="real@email.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
                data-testid="edit-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <Phone className="inline h-3 w-3 mr-1" />Phone
              </label>
              <input
                type="tel"
                value={form.contactPhone}
                onChange={e => setForm({ ...form, contactPhone: e.target.value })}
                placeholder="+34 600 000 000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
                data-testid="edit-phone-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <MessageCircle className="inline h-3 w-3 mr-1" />WhatsApp
            </label>
            <input
              type="tel"
              value={form.contactWhatsApp}
              onChange={e => setForm({ ...form, contactWhatsApp: e.target.value })}
              placeholder="+34 600 000 000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
              data-testid="edit-whatsapp-input"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.outreachNotes}
              onChange={e => setForm({ ...form, outreachNotes: e.target.value })}
              placeholder="Add notes about this outreach..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-400"
              rows={3}
              data-testid="edit-notes-input"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
            data-testid="save-outreach-btn"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminOutreach;
